import { useState } from "react";
import { Play, Pause, Mic, Volume2, Loader2 } from "lucide-react";
import { Card } from "./Card";
import { Button } from "./Button";

export const MeditationPlayer = ({
  meditationAudio,
  audioRef,
  audioPlaying,
  audioProgress,
  voiceVolume,
  setVoiceVolume,
  onTogglePlayback,
  onGenerate,
  generatingAudio
}) => {
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  return (
    <section className="space-y-4 animate-fade-in-up">
      <p className="text-xs uppercase tracking-[0.15em] text-[#6B7280] font-medium">
        Guided Meditation
      </p>
      <Card className="p-6 bg-gradient-to-br from-[#FAFAF7] to-white">
        {meditationAudio ? (
          <div className="space-y-5">
            <audio ref={audioRef} src={meditationAudio.audio_url} preload="auto" />
            
            {/* Player Controls */}
            <div className="flex items-center gap-5">
              <button
                onClick={onTogglePlayback}
                className={`
                  w-16 h-16 rounded-full flex items-center justify-center
                  transition-all duration-300 ease-out
                  shadow-lg hover:shadow-xl
                  ${audioPlaying 
                    ? "bg-[#D4A373] hover:bg-[#C49565]" 
                    : "bg-[#4A6741] hover:bg-[#5A7D4F] hover:shadow-[0_0_24px_rgba(74,103,65,0.25)]"
                  }
                `}
                data-testid="play-meditation-btn"
              >
                {audioPlaying ? (
                  <Pause size={26} className="text-white" />
                ) : (
                  <Play size={26} className="text-white ml-1" />
                )}
              </button>
              
              <div className="flex-1 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[#1F2937] font-medium">Listen to Meditation</span>
                  <span className="text-sm text-[#6B7280]">
                    ~{Math.round(meditationAudio.duration_estimate)}s
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#4A6741] to-[#5A7D4F] rounded-full transition-all duration-300"
                    style={{ width: `${audioProgress}%` }}
                  />
                </div>
              </div>
              
              <button
                onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                className="p-3 hover:bg-[#F3F4F6] rounded-xl transition-colors duration-200"
                data-testid="voice-volume-btn"
              >
                <Volume2 size={22} className="text-[#6B7280]" />
              </button>
            </div>
            
            {/* Volume Slider */}
            {showVolumeSlider && (
              <div className="flex items-center gap-4 px-2 py-3 bg-[#F9FAFB] rounded-xl animate-fade-in">
                <span className="text-sm text-[#6B7280] font-medium w-12">Voice</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={voiceVolume}
                  onChange={(e) => setVoiceVolume(parseInt(e.target.value))}
                  className="flex-1"
                  data-testid="voice-volume-slider"
                />
                <span className="text-sm text-[#6B7280] w-10 text-right">{voiceVolume}%</span>
              </div>
            )}
            
            <p className="text-sm text-[#9CA3AF] text-center italic">
              AI-generated meditation based on today's intention
            </p>
          </div>
        ) : (
          <div className="text-center py-6 space-y-5">
            <div className="w-20 h-20 bg-gradient-to-br from-[#4A6741]/10 to-[#4A6741]/5 rounded-full flex items-center justify-center mx-auto">
              <Mic className="text-[#4A6741]" size={32} />
            </div>
            <div className="space-y-2">
              <p className="text-[#1F2937] font-medium text-lg">Generate Guided Meditation</p>
              <p className="text-sm text-[#6B7280] max-w-xs mx-auto">
                Create a personalized audio meditation from this lesson's intention
              </p>
            </div>
            <Button
              onClick={onGenerate}
              disabled={generatingAudio}
              variant="primary"
              data-testid="generate-meditation-btn"
            >
              {generatingAudio ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Volume2 size={18} />
                  Generate Audio
                </>
              )}
            </Button>
          </div>
        )}
      </Card>
    </section>
  );
};
